import { isEqual } from 'lodash-es'
import { useParams, usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import {
  CartLine,
  CartLineInput,
  GetCartVariantsQuery,
  GetZidiCartVariantsQuery,
  ProductCatalogVariantGroup,
} from '../../../__generated__/graphql/catalog/graphql'
import { createPdpUrl } from '../../../app/_components/navigation/createPdpUrl'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { type TrackedCartItem } from '../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { MappedBundle } from '../../../components/pages/cart/utils'
import { CartStoreLineItem } from '../../../lib/adapters/cart-persistence/useCartStore'
import { mapPropertiesToCustomAttributes } from '../../../lib/cart-client/utils'
import { LegacyGtmVariant } from '../../../lib/hooks/useGoogleTagManager/types'
import { getDollarsAndCentsForAmount } from '../../../lib/utils/currency'
import {
  classifyEmbroideryLine,
  isEmbroiderableProduct,
  isEmbroideryItem,
} from '../../../lib/utils/unorganized'
import { CartItem, HydratedCartItemFields } from '../../../types/figs'

import { type UseCartNewCartItem } from '.'

type ExternalCartItem = Omit<CartStoreLineItem, 'cost' | 'discounts'>

type VariantTypes =
  | NonNullable<GetZidiCartVariantsQuery['variants']>['nodes'][number]
  | GetCartVariantsQuery['productCatalogVariants'][number]

type VariantsBySku = Record<string, VariantTypes>

// A product group is embroiderable if any embroidery option (icon/logo/text) is
// available. The cart variants query selects only `__typename` on these options —
// it probes presence, not values — so a non-null option means "this type is offered".
const isEmbroiderable = (
  options: GetCartVariantsQuery['productCatalogVariants'][number]['group']['embroidery']['options']
): boolean => {
  return Boolean(options.icon || options.logo || options.text)
}
type BundleProgramLine = Pick<CartStoreLineItem, 'cartLineId' | 'quantity' | 'bundleProgram'>

export const mapCartLinesToBundlePrograms = (
  lines: BundleProgramLine[]
): Record<string, MappedBundle> => {
  return lines.reduce<Record<string, MappedBundle>>((acc, curr) => {
    // Generated types return 'Maybe' for all values - safeguard here
    if (
      !curr.bundleProgram ||
      !curr.bundleProgram.bundleProgramHandle ||
      !curr.bundleProgram.minQty ||
      !curr.bundleProgram.discountType ||
      !curr.bundleProgram.discountValue
    )
      return acc

    const {
      cartLineId,
      quantity,
      bundleProgram: { bundleProgramHandle, discountType, discountValue, minQty },
    } = curr

    // default values for comparison if bundleProgram isn't initialized
    if (!acc[bundleProgramHandle]) {
      acc[bundleProgramHandle] = {
        bundleProgramHandle,
        discountType,
        discountValue,
        minQty,
        lines: [],
        totalQty: 0,
        qualifiesForDiscount: false,
      }
    }

    acc[bundleProgramHandle].lines.push({ lineId: cartLineId, quantity })
    acc[bundleProgramHandle].totalQty += quantity
    acc[bundleProgramHandle].qualifiesForDiscount = acc[bundleProgramHandle].totalQty >= minQty

    return acc
  }, {})
}
type BundleDiscount = { discountType: string; discountValue: number }

const buildBundleDiscountLookup = (
  bundlePrograms?: Record<string, MappedBundle> | null
): Record<string, BundleDiscount> => {
  if (!bundlePrograms) return {}

  const lookup: Record<string, BundleDiscount> = {}

  for (const bundle of Object.values(bundlePrograms)) {
    if (!bundle.qualifiesForDiscount) continue
    const { discountType, discountValue } = bundle

    const calculatedValue =
      discountType === 'percent' ? (100 - discountValue) / 100 : discountValue * 100 // percentage multiplier or fixed (in dollars)

    for (const line of bundle.lines) {
      lookup[line.lineId] = { discountType, discountValue: calculatedValue }
    }
  }

  return lookup
}

type PlaceholderOptions = {
  // Category of each SKU being added, from the add input
  categoryBySku: Record<string, string>
}

/** When `placeholders` is given, lines without variant data are flagged `isPlaceholder`. */
export function augmentPersistedCartItemsWithFigsData(
  persistedCartItems: ExternalCartItem[],
  variantsBySku: VariantsBySku,
  locale: string,
  bundlePrograms?: Record<string, MappedBundle> | null,
  placeholders?: PlaceholderOptions
): CartItem[] {
  const bundleDiscountByLineId = buildBundleDiscountLookup(bundlePrograms)

  return persistedCartItems.map((item): CartItem => {
    return augmentCartStoreLineItem(
      item,
      variantsBySku,
      locale,
      bundleDiscountByLineId,
      placeholders
    )
  })
}

export const blankCartItem = {
  isLowInventory: false,
  image: {
    alt: '',
    url: undefined,
  },
  fullPrice: 0,
  effectivePrice: 0,
  finalSale: false,
  embroiderable: false,
  productGroupHandle: '',
  productType: '',
  title: '',
  variantId: '',
  colorId: undefined,
  colorDisplayValue: undefined,
  fitId: undefined,
  fitDisplayValue: undefined,
  sizeId: undefined,
  sizeDisplayValue: undefined,
  shopifyProductId: '',
  shopifyVariantId: '',
  currencyCode: 'USD',
  gender: '',
  swPromoEligible: true,
  categoryId: '',
  availableForSale: true,
  urlPath: '#',
} satisfies Partial<CartItem>

function getBasicFields(item: ExternalCartItem): CartItem {
  return {
    ...blankCartItem,
    key: item.cartLineId,
    cartStoreKey: item.cartLineId,
    sku: item.sku,
    quantity: item.quantity,
    properties: item.attributes.reduce((accumulator, current) => {
      return Object.assign(accumulator, { [current.key]: current.value })
    }, {}),
  }
}

const EMBROIDERY_PRODUCT_TYPE = 'Embroidery'

// Placeholder type: the pending add's category, else inferred from the embroidery attributes
function toPlaceholderCartItem(item: CartItem, { categoryBySku }: PlaceholderOptions): CartItem {
  const kind = classifyEmbroideryLine(item.properties)
  const inferredType = kind === 'embroidery' || kind === 'logoSetup' ? EMBROIDERY_PRODUCT_TYPE : ''
  return { ...item, productType: categoryBySku[item.sku] ?? inferredType, isPlaceholder: true }
}

function getHydratedFields(
  variantData: VariantTypes,
  discountMultiplier: number,
  locale: string
): HydratedCartItemFields {
  if ('group' in variantData) {
    return {
      colorId: variantData.group.color?.id,
      colorDisplayValue: variantData.group.color?.displayValue,
      image: {
        alt: variantData.group.title,
        url: variantData.group.images?.[0]?.url,
      },
      fitDisplayValue: variantData.fit?.id === 'regular' ? null : variantData.fit?.displayValue,
      fitId: variantData.fit?.id,
      fullPrice: getDollarsAndCentsForAmount({
        value: variantData.compareAtPrice?.amount ?? variantData.price.amount,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      shopifyProductId: variantData.shopifyProductId,
      effectivePrice: getDollarsAndCentsForAmount({
        value:
          !!variantData.compareAtPrice?.amount && discountMultiplier < 1
            ? variantData.compareAtPrice.amount * discountMultiplier
            : variantData.price.amount * discountMultiplier,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      productGroupHandle: variantData.group.product.handle,
      sizeId: variantData.size?.id,
      sizeDisplayValue: variantData.size?.displayValue,
      title: variantData.group.title,
      finalSale: variantData.group.finalSale,
      embroiderable: isEmbroiderable(variantData.group.embroidery.options),
      categoryId: variantData.group.primaryCategory.id,
      currencyCode: variantData.price.currencyCode,
      gender: variantData.group.gender,
      availableForSale: variantData.availability !== 'UNAVAILABLE',
      urlPath: variantData.urlPath,
      shopifyVariantId: variantData.shopifyId,
      swPromoEligible: variantData.group.siteWidePromoEligible,
      isLowInventory: variantData.stockState === 'LOW_STOCK',
    }
  } else {
    const price =
      variantData.priceDetails?.discountPrice?.amount ?? variantData.priceDetails!.price!.amount
    const effectivePrice = Math.round(price * discountMultiplier)

    return {
      colorId: variantData.rawColor,
      colorDisplayValue: variantData.color,
      image: {
        alt: variantData.productGroup.title,
        url: variantData.image?.source,
      },
      fitId: variantData.rawFit,
      fitDisplayValue: variantData.rawFit === 'regular' ? null : variantData.fit,
      fullPrice: variantData.priceDetails!.price!.amount,
      shopifyVariantId: variantData.shopifyId,
      shopifyProductId: variantData.externalParentId,
      effectivePrice,
      productGroupHandle: variantData.productGroup.handle,
      sizeId: variantData.rawSize,
      sizeDisplayValue: variantData.size,
      title: variantData.productGroup.title,
      finalSale: variantData.product.finalSale,
      embroiderable: false,
      categoryId: variantData.productGroup.rawCategory,
      currencyCode: variantData.priceDetails!.price!.currency,
      gender: variantData.productGroup.genderCategory,
      availableForSale: variantData.product.availableForSale,
      swPromoEligible: variantData.product.swPromoEligible,
      urlPath: createPdpUrl({
        productGroupHandle: variantData.productGroup.handle,
        collectionHandle: undefined,
        isKit: false,
        isExclusive: variantData.product.exclusive,
        genderKey: variantData.productGroup.genderCategory,
        colorKey: variantData.rawColor,
        fitKey: variantData.rawFit,
        sizeKey: variantData.rawSize,
      }),
      isLowInventory: variantData.isLowInventory,
    }
  }
}

function augmentCartStoreLineItem(
  item: ExternalCartItem,
  variantsBySku: VariantsBySku,
  locale: string,
  bundleDiscountByLineId?: Record<string, BundleDiscount>,
  placeholders?: PlaceholderOptions
): CartItem {
  const variant = variantsBySku[item.sku]
  const basicFields = getBasicFields(item)
  if (!variant) {
    return placeholders ? toPlaceholderCartItem(basicFields, placeholders) : basicFields
  }
  // Compute bundle discount type and value
  // 1 and null are defaults so won't affect final result
  let bundleDiscountMultiplier = 1
  let fixedDiscount = null
  if (item.cartLineId && bundleDiscountByLineId && bundleDiscountByLineId[item.cartLineId]) {
    const { discountType, discountValue } = bundleDiscountByLineId[item.cartLineId]!
    if (discountType === 'percent') {
      bundleDiscountMultiplier = discountValue
    } else fixedDiscount = discountValue
  }

  // Compute kits discount percentage and multiplier (always a %)
  const kitsDiscountPct = parseFloat(
    item.attributes.find(e => e.key === '_kitDiscountPct')?.value ?? ''
  )

  const validKitsDiscountPct =
    !isNaN(kitsDiscountPct) && kitsDiscountPct > 0 && kitsDiscountPct <= 100
  const kitDiscountMultiplier = validKitsDiscountPct ? (100 - kitsDiscountPct) / 100 : 1

  // Defer to kit discount, if it exists
  const finalDiscountMultiplier = validKitsDiscountPct
    ? kitDiscountMultiplier
    : bundleDiscountMultiplier

  // Hydrate fields and apply fixed bundle discount - if applicable
  const hydratedFields = getHydratedFields(variant, finalDiscountMultiplier, locale)

  if (fixedDiscount) {
    hydratedFields.effectivePrice = Math.max(0, hydratedFields.effectivePrice - fixedDiscount)
  }

  return {
    ...basicFields,
    variantId: hydratedFields.shopifyVariantId,
    productType: hydratedFields.categoryId,
    ...hydratedFields,
  }
}

/** Adds `incoming` to the comma-separated `_collectionAttribution` list, deduplicated. */
export const mergeCollectionAttribution = (
  existing: string | undefined,
  incoming: string
): string => Array.from(new Set([...(existing ? existing.split(',') : []), incoming])).join(',')

/** Compares sku and attributes, ignoring attribute order, `__typename` and `_collectionAttribution`. */
export function areCartLinesEqual<
  T extends Partial<CartLine> & Pick<CartLine, 'attributes' | 'sku'>,
>(itemA: T, itemB: T): boolean {
  const comparableAttributes = (item: T) =>
    item.attributes
      ?.filter(attr => attr?.key !== '__typename' && attr?.key !== '_collectionAttribution')
      .sort((a, b) => (a?.key ?? '').localeCompare(b?.key ?? ''))
  return isEqual(
    { attributes: comparableAttributes(itemA), sku: itemA.sku },
    { attributes: comparableAttributes(itemB), sku: itemB.sku }
  )
}

type MakeCartItemOptions = {
  properties?: Record<string, string | undefined>
  quantity?: number
  inTeamStore?: boolean
  attributedPortal:
    | undefined
    | {
        name: string
        itemType: 'PortalLogo' | 'PortalColor' | 'Other'
      }
}

export type MakeCartItemProps = { options: MakeCartItemOptions } & {
  product: {
    handle: string
  }
  variantGroup: {
    siteWidePromoEligible: boolean
    finalSale: boolean
    primaryCategory: Pick<ProductCatalogVariantGroup['primaryCategory'], 'id'>
  }
  variant: {
    shopifyId: string
    shopifyProductId: string
    sku: string
    price: {
      amount: number
      currencyCode: string
    }
    compareAtPrice?: null | {
      amount: number
    }
  }
}

export type EnhanceCartItemProps = { options: MakeCartItemOptions } & {
  item: LegacyGtmVariant // Replace with GtmVariant once LegacyGtmVariant type is deleted.
  finalSale: boolean
}

export function useMakeCartItem() {
  const { locale } = useLocalization()
  const params = useParams()
  const pathname = usePathname()
  const translations = useTranslation()

  const collectionHandle: string | undefined = useMemo(() => {
    if (pathname?.startsWith('/collections') && !(params?.collectionHandle instanceof Array)) {
      return params?.collectionHandle ?? undefined
    }
    return undefined
  }, [params, pathname])

  return useCallback(
    (props: MakeCartItemProps | EnhanceCartItemProps): UseCartNewCartItem[] => {
      const isFinalSale = 'item' in props ? props.finalSale : props.variantGroup.finalSale

      const properties = {
        ...(isFinalSale
          ? { [translations.cart.finalSale]: translations.cart.nonReturnable, _LPROP: 'final sale' }
          : {}),
        ...(collectionHandle ? { _collectionAttribution: collectionHandle } : {}),
        ...(props.options.attributedPortal?.name
          ? { _portalAttribution: props.options.attributedPortal.name }
          : {}),
        ...(props.options.attributedPortal?.itemType === 'PortalColor'
          ? { _isPortalColor: 'true' }
          : {}),
        ...(props.options.attributedPortal?.itemType === 'PortalLogo'
          ? { _isPortalLogo: 'true' }
          : {}),
        ...(props.options.inTeamStore ? { _inTeamStore: 'true' } : {}),
        ...(props.options.properties ?? {}),
      }

      return [
        {
          finalSale: isFinalSale,
          quantity: props.options.quantity || 1,
          properties,
          item: {
            shopifyId: 'item' in props ? props.item.shopifyId : props.variant.shopifyId,
            externalParentId:
              'item' in props ? props.item.externalParentId : props.variant.shopifyProductId,
            currencyType:
              'item' in props ? props.item.currencyType : props.variant.price.currencyCode,
            handle: 'item' in props ? props.item.handle : props.product.handle,
            sku: 'item' in props ? props.item.sku : props.variant.sku,
            category: 'item' in props ? props.item.category : props.variantGroup.primaryCategory.id,
            price:
              'item' in props
                ? props.item.price
                : getDollarsAndCentsForAmount({
                    value: props.variant.compareAtPrice?.amount ?? props.variant.price.amount,
                    unit: 'dollars',
                    currency: props.variant.price.currencyCode,
                    locale: locale,
                  }).cents,
            discountPrice:
              'item' in props
                ? props.item.discountPrice
                : props.variant.compareAtPrice?.amount
                ? getDollarsAndCentsForAmount({
                    value: props.variant.price.amount,
                    unit: 'dollars',
                    currency: props.variant.price.currencyCode,
                    locale: locale,
                  }).cents
                : null,
          },
        },
      ]
    },
    [collectionHandle, locale, translations.cart.finalSale, translations.cart.nonReturnable]
  )
}

type IncomingCartItem = UseCartNewCartItem & { quantity: number }

function getIncomingCartItemFitId(cartItem: IncomingCartItem): string | undefined {
  const fit = (cartItem.item as { fit?: string | { id?: string | null } | null }).fit
  if (typeof fit === 'string') return fit
  return fit?.id ?? undefined
}

function getIncomingProductCatalogTrackingFields(
  cartItem: IncomingCartItem
): Pick<TrackedCartItem, 'colorId' | 'gender' | 'sizeId' | 'imageUrl' | 'availableForSale'> {
  const item = cartItem.item as {
    availability?: string | null
    size?: { id?: string | null } | null
    variantGroup?: {
      color?: { id?: string | null } | null
      gender?: string | null
      images?: Array<{ url?: string | null } | null> | null
    }
  }

  return {
    colorId: item.variantGroup?.color?.id ?? undefined,
    gender: item.variantGroup?.gender ?? undefined,
    sizeId: item.size?.id ?? undefined,
    imageUrl: item.variantGroup?.images?.[0]?.url ?? undefined,
    availableForSale: item.availability !== 'UNAVAILABLE',
  }
}

function getIncomingZidiTrackingFields(
  cartItem: IncomingCartItem
): Pick<TrackedCartItem, 'colorId' | 'gender' | 'sizeId' | 'imageUrl' | 'availableForSale'> {
  const item = cartItem.item as {
    color?: string | null
    genderCategory?: string | null
    image?: string | { source?: string | null } | null
    size?: string | null
    soldOut?: boolean | null
  }

  return {
    colorId: item.color ?? undefined,
    gender: item.genderCategory ?? undefined,
    sizeId: item.size ?? undefined,
    imageUrl: typeof item.image === 'string' ? item.image : item.image?.source ?? undefined,
    availableForSale: item.soldOut === false,
  }
}

export const useConvertCartItemIntoSignalData = () => {
  const { locale } = useLocalization()

  const convertCartItemIntoSignalData = useCallback(
    (cartItem: IncomingCartItem | CartItem): TrackedCartItem => {
      if ('effectivePrice' in cartItem) {
        return {
          variantSoldOut: false,
          effectivePrice: cartItem.effectivePrice,
          quantity: cartItem.quantity,
          productShopifyId: cartItem.shopifyProductId,
          variantShopifyId: cartItem.shopifyVariantId,
          variantSku: cartItem.sku,
          currencyCode: cartItem.currencyCode,
          handle: cartItem.productGroupHandle,
          categoryId: cartItem.categoryId,
          colorId: cartItem.colorId ?? undefined,
          gender: cartItem.gender ?? undefined,
          sizeId: cartItem.sizeId ?? undefined,
          fitId: cartItem.fitId ?? undefined,
          imageUrl: cartItem.image.url,
          availableForSale: cartItem.availableForSale,
          title: cartItem.title,
        }
      } else if ('shopifyProductId' in cartItem.item) {
        const trackingFields = getIncomingProductCatalogTrackingFields(cartItem)

        return {
          variantSoldOut: false,
          effectivePrice: getDollarsAndCentsForAmount({
            value: cartItem.item.price.amount,
            unit: 'dollars',
            currency: cartItem.item.price.currencyCode,
            locale: locale,
          }).cents,
          quantity: cartItem.quantity,
          productShopifyId: cartItem.item.shopifyProductId,
          variantShopifyId: cartItem.item.shopifyId,
          variantSku: cartItem.item.sku,
          currencyCode: cartItem.item.price.currencyCode,
          handle: cartItem.item.product.handle,
          categoryId: cartItem.item.variantGroup.primaryCategory.id,
          ...trackingFields,
          fitId: getIncomingCartItemFitId(cartItem),
        }
      } else {
        const trackingFields = getIncomingZidiTrackingFields(cartItem)

        return {
          variantSoldOut: false,
          effectivePrice: cartItem.item.discountPrice ?? cartItem.item.price ?? 0,
          quantity: cartItem.quantity,
          productShopifyId: cartItem.item.externalParentId,
          variantShopifyId: cartItem.item.shopifyId,
          variantSku: cartItem.item.sku,
          currencyCode: cartItem.item.currencyType,
          handle: cartItem.item.handle,
          categoryId: cartItem.item.category,
          ...trackingFields,
          fitId: getIncomingCartItemFitId(cartItem),
        }
      }
    },
    [locale]
  )

  return convertCartItemIntoSignalData
}

type CartItemsByKey = Record<string, CartItem>

export type CartLineType = 'digitization' | 'embroidery' | 'embroidered' | 'standard'

/** Classifies a cart line; lines without a known embroidery version are embroidered or standard. */
export function getCartLineType(item: CartItem): CartLineType {
  if (!isEmbroideryItem(item)) return isEmbroiderableProduct(item) ? 'embroidered' : 'standard'
  return item.properties._is_logo_setup === 'true' ? 'digitization' : 'embroidery'
}

/** RUM context for a cart add, or undefined when no line in it is embroidery. */
export function getEmbroideryAddToBagContext(
  items: Array<{ properties: CartItem['properties'] }>
): Record<string, string> | undefined {
  const embroideryLines = items.filter(item => {
    const kind = classifyEmbroideryLine(item.properties)
    return kind === 'embroidery' || kind === 'logoSetup'
  })
  if (embroideryLines.length === 0) return undefined
  return {
    embroideryVersion: embroideryLines[0]?.properties._embroidery_version ?? '',
    isUpsell: String(items.some(item => item.properties._embroidery_upsell !== undefined)),
    embroideryLineCount: String(embroideryLines.length),
    hasLogoSetup: String(items.some(item => item.properties._is_logo_setup === 'true')),
  }
}

/** Returns every line sharing a `_guid` (the single seam for future v2 per-line removal). */
export function getGuidGroup(guid: string | undefined, cartItems: CartItemsByKey): CartItem[] {
  return Object.values(cartItems).filter(item => item.properties._guid === guid)
}

/** Returns the logo's setup line when every non-setup line using the logo is being removed. */
export function getLogoDigitizationLineIfUnused(
  logoId: string | undefined,
  removedKeys: ReadonlySet<string>,
  cartItems: CartItemsByKey
): CartItem | undefined {
  const linesWithLogo = Object.values(cartItems).filter(item => item.properties._logo_id === logoId)
  const setupLine = linesWithLogo.find(item => item.properties._is_logo_setup === 'true')
  const isLogoStillInUse = linesWithLogo.some(
    item => item.properties._is_logo_setup !== 'true' && !removedKeys.has(item.key)
  )
  return isLogoStillInUse ? undefined : setupLine
}

/**
 * Returns the garment's properties without embroidery keys, restoring final-sale props when
 * `item.finalSale`. A `newKitId` is written before the final-sale props to keep attribute order.
 */
export function stripEmbroideryAttributes(
  item: CartItem,
  finalSaleKey: string,
  newKitId?: string
): CartItem['properties'] {
  const {
    _embroideryId,
    _guid,
    _LPROP,
    [finalSaleKey]: finalSaleValue,
    _kitId,
    _setUid,
    _logo_id,
    _embroidery_version,
    _embroidery_pricing,
    ...rest
  } = item.properties
  if (newKitId !== undefined) {
    rest._kitId = newKitId
    rest._setUid = newKitId + '1'
  }
  if (item.finalSale) {
    rest._LPROP = _LPROP
    rest[finalSaleKey] = finalSaleValue
  }
  return rest
}

/** Builds a kit id from the SKUs of the kit's non-embroidery lines. */
export function getNewKitId(oldKitId: string | undefined, cartItems: CartItemsByKey): string {
  return (
    Object.values(cartItems)
      .filter(item => item.productType !== 'Embroidery' && item.properties._kitId === oldKitId)
      .map(o => o.sku)
      .sort()
      .join() + ','
  )
}

type PlanCartRemovalInput = {
  cartItems: CartItemsByKey
  keys: string[]
  pendingKeys: ReadonlySet<string>
  removeAssociatedPortalProduct?: boolean
  finalSaleKey: string
}

type PlanCartRemovalOutput = { itemsToRemove: CartItem[]; itemsToAddBack: CartLineInput[] }

/** Plans which lines to remove and which stripped garments to re-add; pure, no store I/O. */
export function planCartRemoval({
  cartItems,
  keys,
  pendingKeys,
  removeAssociatedPortalProduct,
  finalSaleKey,
}: PlanCartRemovalInput): PlanCartRemovalOutput {
  const allItems = Object.values(cartItems)
  const toLineInput = (item: CartItem, properties: CartItem['properties']): CartLineInput => ({
    merchandiseId: item.variantId,
    quantity: item.quantity,
    attributes: mapPropertiesToCustomAttributes(properties),
  })

  // 1. Requested lines: present in the cart and not already being removed
  const requestedKeys = new Set(keys.filter(key => cartItems[key] && !pendingKeys.has(key)))

  // Keyed so cascaded lines dedupe; a pending key is never added
  const removal = new Map<string, CartItem>()
  const addToRemoval = (items: CartItem[]) =>
    items.forEach(item => {
      if (!pendingKeys.has(item.key)) removal.set(item.key, item)
    })
  const getGroup = (item: CartItem) =>
    item.properties._guid ? getGuidGroup(item.properties._guid, cartItems) : [item]

  // 2. Collect related lines
  requestedKeys.forEach(key => {
    const item = cartItems[key]!
    const lineType = getCartLineType(item)
    if (lineType === 'standard') return addToRemoval([item])
    if (lineType === 'digitization') {
      // Every line using the logo goes with its `_guid` group (covers v2 text/icon siblings)
      const logoUsers = allItems.filter(
        other =>
          other.properties._logo_id === item.properties._logo_id &&
          other.properties._is_logo_setup !== 'true'
      )
      return addToRemoval([item, ...logoUsers.flatMap(getGroup)])
    }
    addToRemoval(getGroup(item))
  })

  // 3. Drop the logo's setup line once nothing kept still uses it
  const removedKeys = new Set(removal.keys())
  const logoIds = new Set(
    [...removal.values()].map(item => item.properties._logo_id).filter(Boolean)
  )
  logoIds.forEach(logoId => {
    const setupLine = getLogoDigitizationLineIfUnused(logoId, removedKeys, cartItems)
    if (setupLine) addToRemoval([setupLine])
  })

  // 4. Re-add unrequested garments without embroidery props, unless portal suppression applies
  const reAddGarments = [...removal.values()].filter(
    item =>
      isEmbroiderableProduct(item) &&
      !requestedKeys.has(item.key) &&
      !(
        removeAssociatedPortalProduct &&
        (item.properties._isPortalColor === 'true' || item.properties._inTeamStore === 'true')
      )
  )
  const itemsToAddBack = reAddGarments.map(item =>
    toLineInput(
      item,
      stripEmbroideryAttributes(
        item,
        finalSaleKey,
        item.properties._kitId ? getNewKitId(item.properties._kitId, cartItems) : undefined
      )
    )
  )

  // 5. Re-add the rest of each re-added garment's kit under the new kit id
  const kitIds = new Set(reAddGarments.map(item => item.properties._kitId).filter(Boolean))
  allItems
    .filter(
      item =>
        kitIds.has(item.properties._kitId) &&
        !item.properties._guid &&
        !removal.has(item.key) &&
        !pendingKeys.has(item.key)
    )
    .forEach(item => {
      const { _kitId, _setUid, ...rest } = item.properties
      const newKitId = getNewKitId(_kitId, cartItems)
      rest._kitId = newKitId
      rest._setUid = newKitId + '1'
      addToRemoval([item])
      itemsToAddBack.push(toLineInput(item, rest))
    })

  return { itemsToRemove: [...removal.values()], itemsToAddBack }
}
